import useConvertEstimateToProposal from '@/api/mutations/estimates/useConvertEstimateToProposal';
import useDuplicateEstimate from '@/api/mutations/estimates/useDuplicateEstimate';
import useUnarchiveEstimate from '@/api/mutations/estimates/useUnarchiveEstimate';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import EstimateStatusBadge from '@/ui/components/estimateStatusBadge/EstimateStatusBadge';
import { ContextMenuDropdown, H4, useToastContext } from '@payaca/ambrosia';
import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ArchiveEstimateModal from './ArchiveEstimateModal';

type EstimateHeaderIdentityProps = {
  projectId: string;
  estimateId: string;
  /**
   * Whether to include "Convert to proposal" in the actions menu. Set false
   * where a separate primary Convert button is shown instead (a sent Estimate).
   */
  showConvertInMenu?: boolean;
  /** When provided on a sent Estimate, adds a "Resend" action to the menu. */
  onResend?: () => void;
};

const EstimateHeaderIdentity: FC<EstimateHeaderIdentityProps> = (props) => {
  const { projectId, estimateId, showConvertInMenu = true, onResend } = props;

  const { estimate } = useGetProjectEstimate(projectId, estimateId);

  const history = useHistory();
  const { pushSuccess } = useToastContext();
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const { mutate: unarchive, isPending: isUnarchiving } = useUnarchiveEstimate({
    projectId,
  });
  const { mutate: duplicate, isPending: isDuplicating } = useDuplicateEstimate({
    projectId,
    options: {
      onSuccess: (data) => {
        pushSuccess('Estimate duplicated');
        history.push(
          `/projects/${projectId}/estimates/${data.duplicateEstimate.id}`
        );
      },
    },
  });
  const { mutate: convertToProposal, isPending: isConverting } =
    useConvertEstimateToProposal({
      projectId,
      options: {
        onSuccess: (data) => {
          history.push(
            `/projects/${projectId}/proposals/${data.convertEstimateToProposal.publicId}`
          );
        },
      },
    });

  const isSent = estimate?.status === 'SENT';
  const isDraft = estimate?.status === 'DRAFT';
  const isArchived = estimate?.status === 'ARCHIVED';
  const canArchive = isSent || isDraft;
  // Convert stays available in the menu regardless of whether the Project
  // already has an active Proposal; the backend rejects the conversion with a
  // clear error if it can't proceed. The primary Convert button disables
  // itself in that case, but this menu route stays clickable.
  const canConvertToProposal = isSent || isDraft;

  return (
    <>
      <div className="flex items-center gap-x-2">
        <H4>{estimate?.reference}</H4>

        {estimate?.status && (
          <EstimateStatusBadge
            status={estimate.status}
            sentAt={estimate.sentAt ?? null}
            bouncedAt={estimate.bouncedAt ?? null}
          />
        )}

        <ContextMenuDropdown
          size="sm"
          variant="secondary"
          label="Estimate actions"
          isProcessing={isConverting}
          disabled={isConverting}
          menuItems={[
            ...(onResend && isSent
              ? [
                  {
                    label: 'Resend',
                    disabled: !estimate,
                    onClick: onResend,
                  },
                ]
              : []),
            {
              label: 'Duplicate estimate',
              disabled: !estimate || isDuplicating,
              onClick: () => duplicate({ id: estimateId }),
            },
            ...(canConvertToProposal && showConvertInMenu
              ? [
                  {
                    label: 'Convert to proposal',
                    disabled: !estimate || isConverting,
                    onClick: () => convertToProposal({ id: estimateId }),
                  },
                ]
              : []),
            ...(canArchive
              ? [
                  {
                    label: 'Archive estimate',
                    onClick: () => setIsArchiveModalOpen(true),
                  },
                ]
              : []),
            ...(isArchived
              ? [
                  {
                    label: 'Unarchive estimate',
                    disabled: isUnarchiving,
                    onClick: () => unarchive({ id: estimateId }),
                  },
                ]
              : []),
          ]}
        />
      </div>

      <ArchiveEstimateModal
        projectId={projectId}
        estimateId={estimateId}
        isSent={isSent}
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
      />
    </>
  );
};

export default EstimateHeaderIdentity;
